import React, { useEffect, useState } from "react";
import axios from "axios";

const ManageStudentAccess = () => {
    const [activeTab, setActiveTab] = useState("subject-access");
    const [accessRequests, setAccessRequests] = useState([]);
    const [subscriptionRequests, setSubscriptionRequests] = useState([]);
    const [mockTestRequests, setMockTestRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState("");

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch subject access requests
                const accessResponse = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/payment/all-access-requests`
                );
                setAccessRequests(accessResponse.data);

                // Fetch test subscription requests
                const subResponse = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/payment/test-subscription-requests`
                );
                setSubscriptionRequests(subResponse.data);

                // Fetch mock test requests
                const mockTestResponse = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/auth/mocktest-access-requests`
                );
                setMockTestRequests(mockTestResponse.data);
            } catch (error) {
                console.error("Error fetching requests:", error);
                setMessage("Failed to load requests");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Refresh mock test requests when tab changes
    useEffect(() => {
        if (activeTab === "mocktest-access") {
            const fetchMockTestRequests = async () => {
                try {
                    const response = await axios.get(
                        `${process.env.REACT_APP_BACKEND_URL}/api/auth/mocktest-access-requests`
                    );
                    setMockTestRequests(response.data);
                } catch (error) {
                    console.error("Error fetching mock test requests:", error);
                }
            };
            fetchMockTestRequests();
        }
    }, [activeTab]);

    const approveMockTestAccess = async (studentId, subject) => {
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/approve-mocktest-access`, {
                student_id: studentId,
                subject
            });

            setMockTestRequests(prev =>
                prev.map(request =>
                    request.student_id === studentId && request.subject === subject
                        ? { ...request, approved: 1 }
                        : request
                )
            );

            setMessage(`Mock test access approved for ${subject}`);
        } catch (error) {
            console.error("Error approving mock test access:", error);
            setMessage("Failed to approve mock test access");
        }
    };

    const approveAccess = async (studentId, subject) => {
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/payment/approve-access`, {
                student_id: studentId,
                subject
            });

            setAccessRequests(prev =>
                prev.map(request =>
                    request.student_id === studentId && request.subject === subject
                        ? { ...request, approved: 1 }
                        : request
                )
            );

            setMessage(`Access approved for ${subject}`);
        } catch (error) {
            console.error("Error approving access:", error);
            setMessage("Failed to approve access");
        }
    };

    const approveTestSubscription = async (studentId) => {
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/payment/approve-test-subscription`, {
                student_id: studentId
            });

            setSubscriptionRequests(prev =>
                prev.filter(request => request.student_id !== studentId)
            );

            setMessage(`Test subscription approved for student ${studentId}`);
        } catch (error) {
            console.error("Error approving test subscription:", error);
            setMessage("Failed to approve test subscription");
        }
    };

    if (loading) {
        return <div className="p-4">Loading requests...</div>;
    }

    return (
        <div className="container mx-auto p-4">
            <h1 className="text-2xl font-bold mb-4">Student Access Management</h1>

            <div className="flex border-b mb-6 overflow-x-auto">
                <div className="flex">
                    <button
                        className={`py-2 px-4 font-medium whitespace-nowrap ${activeTab === "subject-access" ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-600"}`}
                        onClick={() => setActiveTab("subject-access")}
                    >
                        Subject Access
                    </button>
                    <button
                        className={`py-2 px-4 font-medium whitespace-nowrap ${activeTab === "mocktest-access" ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-600"}`}
                        onClick={() => setActiveTab("mocktest-access")}
                    >
                        Mock Test Access
                    </button>
                    <button
                        className={`py-2 px-4 font-medium whitespace-nowrap ${activeTab === "test-subscriptions" ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-600"}`}
                        onClick={() => setActiveTab("test-subscriptions")}
                    >
                        Test Subscriptions
                    </button>
                </div>
            </div>

            {message && (
                <div className={`mb-4 p-4 ${
                    message.includes("approved") ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                }`}>
                    {message}
                </div>
            )}

            {activeTab === "subject-access" && (
                accessRequests.length === 0 ? (
                    <p className="p-4">No subject access requests found</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-white">
                            <thead>
                                <tr>
                                    <th className="py-2 px-4 border">Student Name</th>
                                    <th className="py-2 px-4 border">Type</th>
                                    <th className="py-2 px-4 border">Subject</th>
                                    <th className="py-2 px-4 border">Amount</th>
                                    <th className="py-2 px-4 border">Payment Date</th>
                                    <th className="py-2 px-4 border">Status</th>
                                    <th className="py-2 px-4 border">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {accessRequests.map((request) => (
                                    <tr key={`${request.student_id}-${request.subject}`}>
                                        <td className="py-2 px-4 border" data-label="Student">
                                            {request.first_name} {request.last_name}
                                        </td>
                                        <td className="py-2 px-4 border" data-label="Type">{request.type}</td>
                                        <td className="py-2 px-4 border" data-label="Subject">{request.subject}</td>
                                        <td className="py-2 px-4 border" data-label="Amount">${request.amount}</td>
                                        <td className="py-2 px-4 border" data-label="Payment Date">
                                            {new Date(request.payment_date).toLocaleDateString()}
                                        </td>
                                        <td className="py-2 px-4 border" data-label="Status">
                                            {request.approved === 1 ? (
                                                <span className="text-green-600 font-semibold">Approved</span>
                                            ) : (
                                                <span className="text-yellow-600 font-semibold">Pending Approval</span>
                                            )}
                                        </td>
                                        <td className="py-2 px-4 border" data-label="Action">
                                            {request.approved === 0 && (
                                                <button
                                                    onClick={() => approveAccess(request.student_id, request.subject)}
                                                    className="bg-green-500 text-white py-1 px-3 rounded hover:bg-green-600 whitespace-nowrap"
                                                >
                                                    Approve
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            )}

            {activeTab === "mocktest-access" && (
                mockTestRequests.length === 0 ? (
                    <p className="p-4">No mock test access requests found</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-white">
                            <thead>
                                <tr>
                                    <th className="py-2 px-4 border">Student Name</th>
                                    <th className="py-2 px-4 border">Type</th>
                                    <th className="py-2 px-4 border">Subject</th>
                                    <th className="py-2 px-4 border">Amount</th>
                                    <th className="py-2 px-4 border">Payment Date</th>
                                    <th className="py-2 px-4 border">Status</th>
                                    <th className="py-2 px-4 border">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {mockTestRequests.map((request) => (
                                    <tr key={`${request.student_id}-${request.subject}`}>
                                        <td className="py-2 px-4 border" data-label="Student">
                                            {request.first_name} {request.last_name}
                                        </td>
                                        <td className="py-2 px-4 border" data-label="Type">{request.type}</td>
                                        <td className="py-2 px-4 border" data-label="Subject">{request.subject}</td>
                                        <td className="py-2 px-4 border" data-label="Amount">${request.amount}</td>
                                        <td className="py-2 px-4 border" data-label="Payment Date">
                                            {new Date(request.payment_date).toLocaleDateString()}
                                        </td>
                                        <td className="py-2 px-4 border" data-label="Status">
                                            {request.approved === 1 ? (
                                                <span className="text-green-600 font-semibold">Approved</span>
                                            ) : (
                                                <span className="text-yellow-600 font-semibold">Pending Approval</span>
                                            )}
                                        </td>
                                        <td className="py-2 px-4 border" data-label="Action">
                                            {request.approved === 0 && (
                                                <button
                                                    onClick={() => approveMockTestAccess(request.student_id, request.subject)}
                                                    className="bg-green-500 text-white py-1 px-3 rounded hover:bg-green-600 whitespace-nowrap"
                                                >
                                                    Approve
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            )}

            {activeTab === "test-subscriptions" && (
                subscriptionRequests.length === 0 ? (
                    <p className="p-4">No test subscription requests found</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-white">
                            <thead>
                                <tr>
                                    <th className="py-2 px-4 border">Student</th>
                                    <th className="py-2 px-4 border">Subscription ID</th>
                                    <th className="py-2 px-4 border">Period Start</th>
                                    <th className="py-2 px-4 border">Period End</th>
                                    <th className="py-2 px-4 border">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {subscriptionRequests.map((request) => (
                                    <tr key={`${request.student_id}-${request.stripe_subscription_id}`}>
                                        <td className="py-2 px-4 border" data-label="Student">
                                            {request.first_name} {request.last_name}
                                        </td>
                                        <td className="py-2 px-4 border" data-label="Subscription ID">
                                            {request.stripe_subscription_id?.substring(0, 8)}...
                                        </td>
                                        <td className="py-2 px-4 border" data-label="Period Start">
                                            {request.current_period_start ?
                                                new Date(request.current_period_start).toLocaleDateString() : 'N/A'}
                                        </td>
                                        <td className="py-2 px-4 border" data-label="Period End">
                                            {request.current_period_end ?
                                                new Date(request.current_period_end).toLocaleDateString() : 'N/A'}
                                        </td>
                                        <td className="py-2 px-4 border" data-label="Action">
                                            <button
                                                onClick={() => approveTestSubscription(request.student_id)}
                                                className="bg-green-500 text-white py-1 px-3 rounded hover:bg-green-600 whitespace-nowrap"
                                            >
                                                Approve
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            )}

            {/* Add responsive CSS */}
            <style jsx>{`
                @media (max-width: 768px) {
                    table {
                        width: 100%;
                        border-collapse: collapse;
                    }
                    
                    thead {
                        display: none;
                    }
                    
                    tr {
                        display: block;
                        margin-bottom: 1rem;
                        border: 1px solid #e2e8f0;
                        border-radius: 0.25rem;
                    }
                    
                    td {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        padding: 0.5rem 1rem;
                        border-bottom: 1px solid #e2e8f0;
                        text-align: right;
                    }
                    
                    td:before {
                        content: attr(data-label);
                        font-weight: 600;
                        margin-right: 1rem;
                        text-align: left;
                    }
                    
                    td:last-child {
                        border-bottom: 0;
                        justify-content: flex-end;
                    }
                    
                    td:last-child:before {
                        display: none;
                    }
                }
            `}</style>
        </div>
    );
};

export default ManageStudentAccess;